import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Eye, Loader2, X } from 'lucide-react';
import { EXPORT_STATUS_TEXT, type ExportJob, type ReportTemplate } from '@kaba/shared';
import { queryClient } from '../../lib/board';
import { api, ApiError, newOpId } from '../../lib/api';
import { toast } from '../../lib/toast';
import { Button } from '../ui/ui';
import { AttachmentPreview, type PreviewItem } from '../card/AttachmentPreview';

/** Ist die PDF-Erstellung auf diesem Server eingerichtet? */
export function usePdfAvailable() {
  return useQuery({ queryKey: ['pdf-available'], queryFn: () => api.get<{ pdf: boolean }>('/api/exports/available'), staleTime: 10 * 60_000 }).data?.pdf ?? false;
}

/**
 * Download direkt über die Server-Adresse (Content-Disposition: attachment). Kein Blob-Link: der öffnete
 * das PDF in Safari bzw. als installierte Web-App im selben Fenster, ohne Weg zurück.
 */
export const exportDownloadUrl = (job: ExportJob) => `/api/exports/${job.id}/download`;

function exportPreviewItem(job: ExportJob): PreviewItem {
  const url = exportDownloadUrl(job);
  return { id: job.id, filename: `${job.title}.pdf`, mime: 'application/pdf', size: job.size ?? 0, isImage: false, url, thumbUrl: null, downloadUrl: url };
}

/** Auftrag starten und verfolgen. Eine Anforderungs-ID je Klick verhindert Doppelaufträge (A11). */
export function useReportJob() {
  const [job, setJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const active = !!job && (job.status === 'queued' || job.status === 'running');
  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try {
        setJob(await api.get<ExportJob>(`/api/exports/${job!.id}`));
      } catch (e) {
        setError((e as Error).message);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [active, job?.id]);
  const start = async (body: Record<string, unknown>) => {
    if (starting || active) return;
    setStarting(true);
    setError(null);
    try {
      setJob(await api.post<ExportJob>('/api/exports', { ...body, requestId: newOpId().replace(/[^A-Za-z0-9_-]/g, '').padEnd(8, '0') }));
    } catch (e) {
      const other = e instanceof ApiError && e.status === 409 ? (e.data as { details?: { job?: ExportJob } })?.details?.job : null;
      if (other) setJob(other);
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  };
  const cancel = async () => {
    if (!job) return;
    await api.del(`/api/exports/${job.id}`).catch(() => undefined);
    setJob({ ...job, status: 'cancelled' });
  };
  return { job, error, starting, active, start, cancel, reset: () => (setJob(null), setError(null)) };
}

/** Status eines Auftrags mit Download – für Board- und Kartenexport gleich */
export function ReportStatus({ r }: { r: ReturnType<typeof useReportJob> }) {
  const { job, error } = r;
  const [preview, setPreview] = useState(false);
  if (!job && !error) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--line)] bg-[var(--column)] p-3 text-sm" role="status" aria-live="polite">
      {job && (
        <p className="flex items-center gap-2">
          {r.active && <Loader2 size={16} className="animate-spin" aria-hidden />}
          <span>
            <b>{job.title}</b> – {EXPORT_STATUS_TEXT[job.status]}
            {job.status === 'done' && job.pages ? ` · ${job.pages} ${job.pages === 1 ? 'Seite' : 'Seiten'}` : ''}
            {job.status === 'done' && job.size ? ` · ${Math.max(1, Math.round(job.size / 1024)).toLocaleString('de-DE')} KB` : ''}
          </span>
        </p>
      )}
      {job?.status === 'failed' && job.error && <p className="text-rose-800 dark:text-rose-200">{job.error}</p>}
      {job?.blocked && <p className="text-rose-800 dark:text-rose-200">{job.blocked}</p>}
      {error && (!job || job.status !== 'done') && <p className="text-rose-800 dark:text-rose-200">{error}</p>}
      {job?.status === 'done' && !job.blocked && (
        <p className="text-xs text-[var(--ink-soft)]">
          Die Datei steht 24 Stunden zum Herunterladen bereit. Danach erstellst du sie bei Bedarf neu – mit aktuellem Stand.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {job?.status === 'done' && !job.blocked && (
          <>
            <Button variant="primary" onClick={() => setPreview(true)}>
              <Eye size={16} aria-hidden /> PDF ansehen
            </Button>
            <a
              href={exportDownloadUrl(job)}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-[var(--line)] px-3 text-sm font-medium hover:bg-black/[0.04] sm:min-h-9 dark:hover:bg-white/10"
            >
              <Download size={16} aria-hidden /> Herunterladen
            </a>
            <AttachmentPreview items={[exportPreviewItem(job)]} index={preview ? 0 : null} onIndex={() => undefined} onClose={() => setPreview(false)} />
          </>
        )}
        {r.active && (
          <Button onClick={() => void r.cancel()}>
            <X size={16} aria-hidden /> Abbrechen
          </Button>
        )}
      </div>
    </div>
  );
}

/* ---------- Persönliche Berichtsvorlagen ---------- */

export function useReportTemplates(kind: 'board' | 'dashboard', targetId: string, enabled = true) {
  const key = ['report-templates', kind, targetId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<ReportTemplate[]>(`/api/report-templates?${kind === 'board' ? 'boardId' : 'dashboardId'}=${targetId}`),
    enabled,
    staleTime: 60_000,
  });
  const save = async (name: string, config: Record<string, unknown>) => {
    await api.post('/api/report-templates', { kind, name, targetId, config });
    await queryClient.invalidateQueries({ queryKey: key });
    toast.info(`Vorlage „${name}“ gespeichert.`);
  };
  const remove = async (id: string) => {
    await api.del(`/api/report-templates/${id}`);
    await queryClient.invalidateQueries({ queryKey: key });
  };
  return { templates: q.data ?? [], save, remove };
}

/** Vorlage wählen, speichern, löschen – speichert nur Optionen, nie Daten */
export function TemplateBar({ t, onApply, current }: { t: ReturnType<typeof useReportTemplates>; onApply: (config: Record<string, unknown>) => void; current: () => Record<string, unknown> | null }) {
  const [sel, setSel] = useState('');
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
          <span className="shrink-0 font-semibold">Vorlage</span>
          <select
            className="min-h-11 min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-sm sm:min-h-9"
            value={sel}
            onChange={(e) => {
              setSel(e.target.value);
              const tp = t.templates.find((x) => x.id === e.target.value);
              if (tp) onApply(tp.config);
            }}
          >
            <option value="">{t.templates.length ? 'Vorlage wählen …' : 'Noch keine gespeichert'}</option>
            {t.templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        {sel && (
          <Button
            variant="ghost"
            onClick={async () => {
              await t.remove(sel);
              setSel('');
            }}
          >
            Löschen
          </Button>
        )}
        {!naming && (
          <Button variant="ghost" onClick={() => setNaming(true)}>
            Als Vorlage speichern …
          </Button>
        )}
      </div>
      {naming && (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const cfg = current();
            if (!cfg || !name.trim()) return;
            try {
              await t.save(name.trim(), cfg);
              setNaming(false);
              setName('');
            } catch (err) {
              toast.error((err as Error).message);
            }
          }}
        >
          <input aria-label="Name der Vorlage" className="min-h-11 min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-sm sm:min-h-9" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Wochenbericht" autoFocus />
          <Button type="submit" disabled={!name.trim()}>
            Speichern
          </Button>
          <Button type="button" variant="ghost" onClick={() => setNaming(false)}>
            Abbrechen
          </Button>
        </form>
      )}
      <p className="text-xs text-[var(--ink-soft)]">Vorlagen speichern nur Einstellungen. Zeiträume wie „nächste 7 Tage“ gelten immer ab dem Tag der Erstellung.</p>
    </div>
  );
}
